import { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { MapPin, Plus, Pencil, Trash2, Star, Loader2, LocateFixed, Phone, Mail } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function SavedAddresses({ token, onSelect, selectable = false, compact = false }) {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [locating, setLocating] = useState(false);
  const [form, setForm] = useState({
    label: '', address_line: '', city: '', state: '', pincode: '',
    phone: '', email: '', lat: null, lng: null, is_default: false
  });

  const fetchAddresses = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/addresses/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setAddresses(data.addresses || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchAddresses(); }, [fetchAddresses]);

  const resetForm = () => {
    setForm({ label: '', address_line: '', city: '', state: '', pincode: '', phone: '', email: '', lat: null, lng: null, is_default: false });
    setEditing(null);
    setShowForm(false);
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) { toast.error('Geolocation not supported'); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        setForm(f => ({ ...f, lat: latitude, lng: longitude }));
        try {
          const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`);
          const data = await res.json();
          if (data.address) {
            setForm(f => ({
              ...f,
              address_line: data.display_name || f.address_line,
              city: data.address.city || data.address.town || data.address.village || f.city,
              state: data.address.state || f.state,
              pincode: data.address.postcode || f.pincode,
            }));
          }
        } catch (err) { console.error(err); }
        setLocating(false);
        toast.success('Location captured');
      },
      () => { setLocating(false); toast.error('Failed to get location'); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const saveAddress = async () => {
    if (!form.label || !form.address_line) { toast.error('Label and address are required'); return; }
    setSaving(true);
    try {
      const url = editing ? `${API}/api/addresses/${editing}` : `${API}/api/addresses/`;
      const method = editing ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(form)
      });
      if (res.ok) {
        toast.success(editing ? 'Address updated' : 'Address saved');
        resetForm();
        fetchAddresses();
      } else {
        const d = await res.json();
        toast.error(d.detail || 'Failed to save');
      }
    } catch { toast.error('Network error'); }
    setSaving(false);
  };

  const deleteAddress = async (id) => {
    try {
      await fetch(`${API}/api/addresses/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Address deleted');
      fetchAddresses();
    } catch { toast.error('Failed to delete'); }
  };

  const editAddress = (addr) => {
    setForm({
      label: addr.label, address_line: addr.address_line, city: addr.city || '',
      state: addr.state || '', pincode: addr.pincode || '', phone: addr.phone || '',
      email: addr.email || '', lat: addr.lat, lng: addr.lng, is_default: addr.is_default
    });
    setEditing(addr.id);
    setShowForm(true);
  };

  if (loading) return <div className="flex items-center gap-2 py-4"><Loader2 className="h-4 w-4 animate-spin" /> Loading addresses...</div>;

  return (
    <div className="space-y-3" data-testid="saved-addresses">
      <div className="flex items-center justify-between">
        {!compact && <h3 className="font-semibold text-sm text-slate-700">Saved Addresses</h3>}
        <Button size="sm" variant="outline" onClick={() => { resetForm(); setShowForm(true); }} data-testid="add-address-btn">
          <Plus className="h-3 w-3 mr-1" /> Add Address
        </Button>
      </div>

      {addresses.length === 0 && (
        <p className="text-sm text-slate-400 py-2">No saved addresses yet</p>
      )}

      <div className={compact ? "space-y-2" : "grid gap-3 sm:grid-cols-2"}>
        {addresses.map(addr => (
          <div
            key={addr.id}
            className={`p-3 border rounded-lg text-sm ${selectable ? 'cursor-pointer hover:border-teal-400' : ''} ${addr.is_default ? 'border-teal-300 bg-teal-50/50' : 'bg-white'}`}
            onClick={() => selectable && onSelect?.(addr)}
            data-testid={`address-${addr.id}`}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-slate-800">{addr.label}</span>
                  {addr.is_default && <Badge className="bg-teal-600 text-xs">Default</Badge>}
                  {addr.lat && <MapPin className="h-3 w-3 text-teal-600" />}
                </div>
                <p className="text-slate-600 mt-1">{addr.address_line}</p>
                {(addr.city || addr.state || addr.pincode) && (
                  <p className="text-slate-500 text-xs">{[addr.city, addr.state, addr.pincode].filter(Boolean).join(', ')}</p>
                )}
                {addr.phone && <p className="text-xs text-slate-400 mt-1 flex items-center gap-1"><Phone className="h-3 w-3" />{addr.phone}</p>}
              </div>
              {!selectable && (
                <div className="flex gap-1">
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => editAddress(addr)} data-testid={`edit-addr-${addr.id}`}>
                    <Pencil className="h-3 w-3" />
                  </Button>
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => deleteAddress(addr.id)} data-testid={`delete-addr-${addr.id}`}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={showForm} onOpenChange={(o) => { if (!o) resetForm(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Address' : 'Add New Address'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Label *</Label>
                <Input placeholder="Home, Office, Clinic..." value={form.label} onChange={e => setForm(f => ({...f, label: e.target.value}))} data-testid="addr-label" />
              </div>
              <div>
                <Label className="text-xs">Phone</Label>
                <Input placeholder="Phone number" value={form.phone} onChange={e => setForm(f => ({...f, phone: e.target.value}))} data-testid="addr-phone" />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-xs">Address *</Label>
                <Button size="sm" variant="ghost" onClick={getCurrentLocation} disabled={locating} className="h-6 text-xs">
                  {locating ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <LocateFixed className="h-3 w-3 mr-1" />}
                  {locating ? 'Fetching...' : 'Use GPS'}
                </Button>
              </div>
              <Input placeholder="Full address" value={form.address_line} onChange={e => setForm(f => ({...f, address_line: e.target.value}))} data-testid="addr-line" />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="text-xs">City</Label>
                <Input placeholder="City" value={form.city} onChange={e => setForm(f => ({...f, city: e.target.value}))} />
              </div>
              <div>
                <Label className="text-xs">State</Label>
                <Input placeholder="State" value={form.state} onChange={e => setForm(f => ({...f, state: e.target.value}))} />
              </div>
              <div>
                <Label className="text-xs">Pincode</Label>
                <Input placeholder="Pincode" value={form.pincode} onChange={e => setForm(f => ({...f, pincode: e.target.value}))} />
              </div>
            </div>
            <div>
              <Label className="text-xs">Email (optional)</Label>
              <Input placeholder="Email" value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} />
            </div>
            {form.lat && (
              <p className="text-xs text-teal-600 flex items-center gap-1">
                <MapPin className="h-3 w-3" /> Location: {form.lat.toFixed(4)}, {form.lng.toFixed(4)}
              </p>
            )}
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.is_default} onChange={e => setForm(f => ({...f, is_default: e.target.checked}))} />
              Set as default address
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={resetForm}>Cancel</Button>
            <Button onClick={saveAddress} disabled={saving} data-testid="save-address-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              {editing ? 'Update' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
